import classNames from 'classnames'
import { FC, useState } from 'react'

type DuaRepeatCounterProps = {
  repeat: number
}

const DuaRepeatCounter: FC<DuaRepeatCounterProps> = ({ repeat }) => {
  const [counter, setCounter] = useState(0)

  const handleClick = () => {
    if (counter < repeat) {
      setCounter(counter + 1)
    }
  }

  const progress = (counter / repeat) * 100
  const isCompleted = counter >= repeat

  // SVG Ring constants
  const radius = 12
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (progress / 100) * circumference

  return (
    <button
      onClick={handleClick}
      className={classNames(
        'group relative flex items-center gap-3 overflow-hidden rounded-full py-1.5 pl-2 pr-4 transition-all duration-300 active:scale-95',
        'bg-slate-200/50 backdrop-blur-md dark:bg-slate-800/40',
        'border border-slate-300/30 dark:border-slate-700/30',
        {
          'ring-2 ring-amber-400/50 shadow-[0_0_15px_rgba(251,191,36,0.3)]': isCompleted,
          'hover:bg-slate-200/80 dark:hover:bg-slate-800/60': !isCompleted,
        },
      )}
    >
      <div className="relative flex h-8 w-8 items-center justify-center">
        {/* Background Ring */}
        <svg className="absolute h-full w-full -rotate-90 transform">
          <circle
            cx="16"
            cy="16"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="transparent"
            className="text-slate-300/30 dark:text-slate-600/30"
          />
          {/* Progress Ring */}
          <circle
            cx="16"
            cy="16"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="transparent"
            strokeDasharray={circumference}
            style={{
              strokeDashoffset: offset,
              transition: 'stroke-dashoffset 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            className={classNames('transition-colors duration-500', {
              'text-amber-500': !isCompleted,
              'text-emerald-500': isCompleted,
            })}
          />
        </svg>
        <span
          className={classNames(
            'text-[10px] font-bold transition-colors duration-300',
            isCompleted
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-slate-600 dark:text-slate-400',
          )}
        >
          {counter}
        </span>
      </div>

      <span
        className={classNames(
          'text-xs font-semibold tracking-tight transition-colors duration-300',
          isCompleted
            ? 'text-emerald-700 dark:text-emerald-300'
            : 'text-slate-700 dark:text-slate-300',
        )}
      >
        {repeat} defa
      </span>

      {/* Subtle completion glow overlay */}
      {isCompleted && <div className="absolute inset-0 bg-emerald-500/5 animate-pulse" />}
    </button>
  )
}

export default DuaRepeatCounter
